import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, useTransform, useSpring, useMotionValue } from 'framer-motion';

/* ── Constants ──────────────────────────────────────────────────────────── */
const TOTAL_IMAGES   = 20;
const MOBILE_IMAGES  = 8;
const MAX_SCROLL     = 3000;
const IMG_WIDTH      = 65;
const IMG_HEIGHT     = 90;

/* ── L.U.N.E phase labels for card back faces ───────────────────────────── */
const LUNE_LABELS = ['L', 'U', 'N', 'E'];
const LUNE_NAMES  = ['Logic', 'Utility', 'Network', 'Execution'];
const LUNE_COLORS = ['#6366f1', '#06b6d4', '#8b5cf6', '#f59e0b'];

/* ── Tech images — Unsplash ─────────────────────────────────────────────── */
const IMAGES = [
  'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=300&q=70',
  'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=300&q=70',
  'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=300&q=70',
  'https://images.unsplash.com/photo-1518770660439-4636190af475?w=300&q=70',
  'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=300&q=70',
  'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=300&q=70',
  'https://images.unsplash.com/photo-1536104968055-4d61aa56f46a?w=300&q=70',
  'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=300&q=70',
  'https://images.unsplash.com/photo-1607799279861-4dd421887fb3?w=300&q=70',
  'https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=300&q=70',
  'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=300&q=70',
  'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&q=70',
  'https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?w=300&q=70',
  'https://images.unsplash.com/photo-1617042375876-a13e36732a04?w=300&q=70',
  'https://images.unsplash.com/photo-1476357471311-43c0db9fb2b4?w=300&q=70',
  'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=300&q=70',
  'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?w=300&q=70',
  'https://images.unsplash.com/photo-1484417894907-623942c8ee29?w=300&q=70',
  'https://images.unsplash.com/photo-1560732488-6b0df240254a?w=300&q=70',
  'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=300&q=70',
];

/* Mobile-only images (first 8, even smaller) */
const IMAGES_MOB = IMAGES.slice(0, MOBILE_IMAGES).map(url =>
  url.replace('w=300', 'w=150').replace('q=70', 'q=60')
);

const lerp = (a, b, t) => a * (1 - t) + b * t;

/* ── FlipCard ───────────────────────────────────────────────────────────── */
function FlipCard({ src, index, target }) {
  const luneIdx   = index % 4;
  const luneColor = LUNE_COLORS[luneIdx];

  return (
    <motion.div
      animate={{ x: target.x, y: target.y, rotate: target.rotation, scale: target.scale, opacity: target.opacity }}
      transition={{ type: 'spring', stiffness: 40, damping: 15 }}
      style={{
        position: 'absolute',
        width: IMG_WIDTH,
        height: IMG_HEIGHT,
        transformStyle: 'preserve-3d',
        perspective: '1000px',
        cursor: 'pointer',
      }}
    >
      <motion.div
        style={{ position: 'relative', width: '100%', height: '100%', transformStyle: 'preserve-3d' }}
        transition={{ duration: 0.6, type: 'spring', stiffness: 260, damping: 20 }}
        whileHover={{ rotateY: 180 }}
      >
        {/* Front */}
        <div style={{
          position: 'absolute', inset: 0,
          overflow: 'hidden', borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
          backfaceVisibility: 'hidden',
          background: '#1a1a2e',
        }}>
          <img src={src} alt={`lune-${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.15)' }} />
        </div>

        {/* Back */}
        <div style={{
          position: 'absolute', inset: 0,
          overflow: 'hidden', borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
          backfaceVisibility: 'hidden',
          transform: 'rotateY(180deg)',
          background: '#0d0d1a',
          border: `1px solid ${luneColor}44`,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: '4px',
        }}>
          <span style={{
            fontSize: '1.4rem', fontWeight: 900,
            color: luneColor, fontFamily: "'Space Grotesk', sans-serif",
            letterSpacing: '-0.04em',
          }}>{LUNE_LABELS[luneIdx]}</span>
          <span style={{
            fontSize: '0.5rem', color: 'rgba(255,255,255,0.5)',
            letterSpacing: '0.15em', fontFamily: "'Space Mono', monospace",
            textTransform: 'uppercase',
          }}>{LUNE_NAMES[luneIdx]}</span>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Main Component ─────────────────────────────────────────────────────── */
export function ScrollMorphHero() {
  const [introPhase, setIntroPhase]       = useState('scatter');
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [isMob, setIsMob]                 = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const check = () => setIsMob(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const activeImages = isMob ? IMAGES_MOB : IMAGES;
  const activeCount  = activeImages.length;

  /* container size */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    ro.observe(el);
    setContainerSize({ width: el.offsetWidth, height: el.offsetHeight });
    return () => ro.disconnect();
  }, []);

  /* virtual scroll */
  const virtualScroll = useMotionValue(0);
  const scrollRef     = useRef(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      const delta   = e.deltaY;
      const next    = Math.min(Math.max(scrollRef.current + delta, 0), MAX_SCROLL);
      const changed = next !== scrollRef.current;
      if (changed) {
        e.preventDefault(); // only intercept when animation can still progress
        scrollRef.current = next;
        virtualScroll.set(next);
      }
      // When at MAX_SCROLL scrolling down: let browser scroll naturally (no preventDefault)
    };

    let touchY = 0;
    const handleTouchStart = (e) => { touchY = e.touches[0].clientY; };
    const handleTouchMove  = (e) => {
      const delta = touchY - e.touches[0].clientY;
      touchY      = e.touches[0].clientY;
      const next  = Math.min(Math.max(scrollRef.current + delta, 0), MAX_SCROLL);
      if (next !== scrollRef.current) {
        e.preventDefault();
        scrollRef.current = next;
        virtualScroll.set(next);
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('touchstart', handleTouchStart, { passive: false });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
    };
  }, [virtualScroll]);

  const morphProgress   = useTransform(virtualScroll, [0, 600],   [0, 1]);
  const smoothMorph     = useSpring(morphProgress,   { stiffness: isMob ? 30 : 40, damping: isMob ? 18 : 20 });
  const scrollRotate    = useTransform(virtualScroll, [600, 3000], [0, 360]);
  const smoothScrollRot = useSpring(scrollRotate,    { stiffness: isMob ? 30 : 40, damping: isMob ? 18 : 20 });
  const mouseX          = useMotionValue(0);
  const smoothMouseX    = useSpring(mouseX,           { stiffness: isMob ? 20 : 30, damping: isMob ? 18 : 20 });

  /* mouse parallax */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleMove = (e) => {
      const rect = el.getBoundingClientRect();
      const norm = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouseX.set(norm * 80);
    };
    el.addEventListener('mousemove', handleMove);
    return () => el.removeEventListener('mousemove', handleMove);
  }, [mouseX]);

  /* intro sequence */
  useEffect(() => {
    const t1 = setTimeout(() => setIntroPhase('line'),   500);
    const t2 = setTimeout(() => setIntroPhase('circle'), 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  /* scatter positions (stable) */
  const scatterPositions = useMemo(() =>
    activeImages.map(() => ({
      x: (Math.random() - 0.5) * 1600,
      y: (Math.random() - 0.5) * 1000,
      rotation: (Math.random() - 0.5) * 180,
      scale: 0.6,
      opacity: 0,
    })), [activeImages]);

  /* live render values */
  const [morphVal,    setMorphVal]    = useState(0);
  const [rotateVal,   setRotateVal]   = useState(0);
  const [parallaxVal, setParallaxVal] = useState(0);

  useEffect(() => {
    const u1 = smoothMorph.on('change', setMorphVal);
    const u2 = smoothScrollRot.on('change', setRotateVal);
    const u3 = smoothMouseX.on('change', setParallaxVal);
    return () => { u1(); u2(); u3(); };
  }, [smoothMorph, smoothScrollRot, smoothMouseX]);

  const contentOpacity = useTransform(smoothMorph, [0.8, 1], [0, 1]);
  const contentY       = useTransform(smoothMorph, [0.8, 1], [20, 0]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative', width: '100%', height: '100%',
        background: '#060608', overflow: 'hidden',
        fontFamily: "'Space Grotesk', sans-serif",
      }}
    >
      <div style={{
        display: 'flex', width: '100%', height: '100%',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        perspective: '1000px',
      }}>

        {/* Intro text */}
        <div style={{
          position: 'absolute', zIndex: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', textAlign: 'center', pointerEvents: 'none',
          top: '50%', transform: 'translateY(-50%)',
        }}>
          <motion.h1
            initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
            animate={introPhase === 'circle' && morphVal < 0.5
              ? { opacity: 1 - morphVal * 2, y: 0, filter: 'blur(0px)' }
              : { opacity: 0, filter: 'blur(10px)' }}
            transition={{ duration: 1 }}
            style={{
              fontSize: 'clamp(1.2rem, 3.5vw, 2.2rem)',
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '-0.04em',
              margin: 0,
              lineHeight: 1.1,
            }}
          >
            Studio Digital<br />untuk Bisnis Anda.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={introPhase === 'circle' && morphVal < 0.4
              ? { opacity: 0.45 - morphVal }
              : { opacity: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            style={{
              marginTop: '1rem',
              fontSize: '0.65rem',
              fontFamily: "'Space Mono', monospace",
              letterSpacing: '0.25em',
              color: 'rgba(255,255,255,0.4)',
              textTransform: 'uppercase',
            }}
          >
            WEBSITE · APLIKASI · SISTEM
          </motion.p>
        </div>

        {/* Arc content */}
        <motion.div
          style={{
            opacity: contentOpacity, y: contentY,
            position: 'absolute', top: '8%', zIndex: 10,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            textAlign: 'center', pointerEvents: 'none', padding: '0 2rem',
          }}
        >
          <p style={{
            fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)',
            letterSpacing: '0.25em', fontFamily: "'Space Mono', monospace",
            textTransform: 'uppercase', marginBottom: '0.75rem',
          }}>CODELUNE · INDONESIA</p>
          <h2 style={{
            fontSize: 'clamp(1.6rem, 3.8vw, 2.6rem)',
            fontWeight: 700, color: '#fff',
            letterSpacing: '-0.03em', lineHeight: 1.05,
            margin: '0 0 1rem',
          }}>
            Kami Bangun Website,<br />Aplikasi & Sistem Digital
          </h2>
          <p style={{
            fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)',
            maxWidth: '30rem', lineHeight: 1.75,
            fontFamily: "'Inter', sans-serif",
          }}>
            Dari website restoran hingga sistem booking dan manajemen bisnis —
            CodeLune mengubah ide Anda menjadi produk digital yang fungsional dan memukau.
          </p>

          {/* Service tags */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.4rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            {['Website', 'Booking System', 'Sistem UMKM', 'Branding Digital'].map((tag) => (
              <span key={tag} style={{
                fontSize: '0.58rem', fontFamily: "'Space Mono', monospace",
                letterSpacing: '0.12em', textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.55)',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)',
                padding: '4px 12px', borderRadius: '20px',
              }}>
                {tag}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Cards */}
        <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {activeImages.map((src, i) => {
            let target = { x: 0, y: 0, rotation: 0, scale: 1, opacity: 1 };

            if (introPhase === 'scatter') {
              target = scatterPositions[i];
            } else if (introPhase === 'line') {
              const spacing = 72;
              const totalW  = activeCount * spacing;
              target = { x: i * spacing - totalW / 2, y: 0, rotation: 0, scale: 1, opacity: 1 };
            } else {
              const isMob     = containerSize.width < 768;
              const minDim    = Math.min(containerSize.width, containerSize.height);
              const circleR   = Math.min(minDim * 0.35, 340);
              const cAngle    = (i / activeCount) * 360;
              const cRad      = (cAngle * Math.PI) / 180;
              const circlePos = {
                x: Math.cos(cRad) * circleR,
                y: Math.sin(cRad) * circleR,
                rotation: cAngle + 90,
              };

              const baseR      = Math.min(containerSize.width, containerSize.height * 1.5);
              const arcR       = baseR * (isMob ? 1.4 : 1.1);
              const arcApexY   = containerSize.height * (isMob ? 0.35 : 0.28);
              const arcCenterY = arcApexY + arcR;
              const spread     = isMob ? 100 : 130;
              const startAngle = -90 - spread / 2;
              const step       = spread / (activeCount - 1);
              const scrollProg = Math.min(Math.max(rotateVal / 360, 0), 1);
              const bounded    = -scrollProg * spread * 0.8;
              const arcAngle   = startAngle + i * step + bounded;
              const arcRad     = (arcAngle * Math.PI) / 180;
              const arcPos     = {
                x: Math.cos(arcRad) * arcR + parallaxVal,
                y: Math.sin(arcRad) * arcR + arcCenterY,
                rotation: arcAngle + 90,
                scale: isMob ? 1.4 : 1.85,
              };

              target = {
                x:        lerp(circlePos.x, arcPos.x, morphVal),
                y:        lerp(circlePos.y, arcPos.y, morphVal),
                rotation: lerp(circlePos.rotation, arcPos.rotation, morphVal),
                scale:    lerp(1, arcPos.scale, morphVal),
                opacity:  1,
              };
            }

            return <FlipCard key={i} src={src} index={i} target={target} />;
          })}
        </div>

        {/* Scroll hint (only before morph starts) */}
        {morphVal < 0.05 && introPhase === 'circle' && (
          <div style={{
            position: 'absolute', bottom: '2rem',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
            pointerEvents: 'none',
          }}>
            <div style={{
              width: '1px', height: '32px',
              background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.3))',
              animation: 'scrollHint 1.5s ease-in-out infinite',
            }} />
            <span style={{
              fontSize: '0.55rem', color: 'rgba(255,255,255,0.25)',
              letterSpacing: '0.2em', fontFamily: "'Space Mono', monospace",
              textTransform: 'uppercase',
            }}>JELAJAHI LAYANAN</span>
          </div>
        )}
      </div>

      <style>{`
        @keyframes scrollHint {
          0%, 100% { opacity: 0.3; transform: translateY(0); }
          50% { opacity: 0.8; transform: translateY(6px); }
        }
      `}</style>
    </div>
  );
}
